import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Drawer, Form, Input, InputNumber, Popconfirm, Select } from 'antd';
import { deleteStudent, listStudents, updateStudent } from '../../api/students';
import { listResidences } from '../../api/residences';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard from '../../components/TableCard';
import { SelectWrap } from '../../components/ToolbarControls';
import { StudentStatusTag, EditIcon, DeleteIcon } from '../../components/UIKit';

export default function AdminStudents() {
  const { message, notification } = App.useApp();
  const [rows, setRows] = useState([]);
  const [residences, setResidences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [residenceId, setResidenceId] = useState(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const [students, allResidences] = await Promise.all([
        listStudents(residenceId ? { residence_id: residenceId } : {}),
        listResidences({}),
      ]);
      setRows(Array.isArray(students) ? students : []);
      setResidences(Array.isArray(allResidences) ? allResidences : []);
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les étudiants.'), placement: 'topRight' });
      setRows([]);
    } finally { setLoading(false); }
  }, [notification, residenceId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filteredRows = useMemo(() => {
    const withRooms = rows.filter(r => r.RoomAssignments && r.RoomAssignments.length > 0);
    const q = search.trim().toLowerCase();
    if (!q) return withRooms;
    return withRooms.filter(r => {
      const nom = (r.User?.nom || '').toLowerCase();
      const prenom = (r.User?.prenom || '').toLowerCase();
      const email = (r.User?.email || '').toLowerCase();
      const code = (r.student_code || '').toLowerCase();
      const residence = (r.Residence?.nom || '').toLowerCase();
      const chambre = (r.RoomAssignments?.[0]?.Room?.code || '').toLowerCase();
      return nom.includes(q) || prenom.includes(q) || email.includes(q) ||
             code.includes(q) || residence.includes(q) || chambre.includes(q);
    });
  }, [rows, search]);

  const onEdit = useCallback((record) => {
    setEditing(record);
    form.setFieldsValue({ student_code: record.student_code, age: record.age, parent_phone: record.parent_phone, residence_id: record.residence_id, gender: record.gender });
    setOpen(true);
  }, [form]);

  const onDelete = useCallback(async (record) => {
    try { await deleteStudent(record.id); message.success('Étudiant supprimé.'); fetchRows(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Suppression échouée.'), placement: 'topRight' }); }
  }, [fetchRows, message, notification]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      await updateStudent(editing.id, values);
      message.success('Étudiant modifié.');
      setOpen(false); setEditing(null); fetchRows();
    } catch (e) { if (e?.response || e?.message) notification.error({ message: getApiErrorMessage(e, 'Modification échouée.'), placement: 'topRight' }); }
  };

  return (
    <div className="page">
      <TableCard
        label="Gestion des étudiants"
        loading={loading}
        rows={filteredRows}
        toolbar={
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flex: 1 }}>
            <input
              className="search-input"
              placeholder="Rechercher un étudiant (ID, nom, email, résidence, chambre)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 260 }}
            />
            <SelectWrap
              value={residenceId || ''}
              onChange={(v) => setResidenceId(v || null)}
              options={[{ value: '', label: 'Toutes les résidences' }, ...residences.map((r) => ({ value: r.id, label: r.nom }))]}
              style={{ minWidth: 200 }}
            />
          </div>
        }
      >
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th>ID étudiant</th>
              <th>Nom</th>
              <th>Prénom</th>
              <th>Email</th>
              <th>Sexe</th>
              <th>Âge</th>
              <th>Parent</th>
              <th>Chambre</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id}>
                <td>{r.student_code || '-'}</td>
                <td>{r.User?.nom || '-'}</td>
                <td>{r.User?.prenom || '-'}</td>
                <td>{r.User?.email || '-'}</td>
                <td>{r.gender || '-'}</td>
                <td>{r.age ?? '-'}</td>
                <td>{r.parent_phone || '-'}</td>
                <td>{r.RoomAssignments?.[0]?.Room?.code || '-'}</td>
                <td>{r.statut ? <StudentStatusTag status={r.statut} /> : '-'}</td>
                <td>
                  <div className="actions-cell">
                    <button className="action-btn" title="Modifier" onClick={() => onEdit(r)}><EditIcon /></button>
                    <Popconfirm title="Supprimer cet étudiant ?" description={r?.User?.email} okText="Supprimer" cancelText="Annuler" okType="danger" onConfirm={() => onDelete(r)}>
                      <button className="action-btn danger" title="Supprimer"><DeleteIcon /></button>
                    </Popconfirm>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </TableCard>

      <Drawer title="Modifier étudiant" placement="right" width={460} onClose={() => setOpen(false)} open={open} destroyOnClose extra={<div style={{ display: 'flex', gap: 8 }}><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={onSubmit}>Enregistrer</button></div>}>
        <Form form={form} layout="vertical">
          <Form.Item name="student_code" label="ID étudiant" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="gender" label="Sexe">
            <Select options={[{ value: 'H', label: 'Homme (H)' }, { value: 'F', label: 'Femme (F)' }]} allowClear />
          </Form.Item>
          <Form.Item name="age" label="Âge"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="parent_phone" label="Parent (contact)"><Input /></Form.Item>
          <Form.Item name="residence_id" label="Résidence" rules={[{ required: true }]}>
            <Select options={residences.map((r) => ({ value: r.id, label: r.nom }))} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
